'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIGroupedTypeahead from 'UIComponents/typeahead/UIGroupedTypeahead';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import ResolveReferences from 'reference-resolvers/ResolveReferences';
import { COMPANIES_SAVED_FILTERS, CONTACTS_SAVED_FILTERS, DEALS_SAVED_FILTERS } from 'reference-resolvers/constants/ReferenceObjectTypes';
import * as ReferenceTypes from 'reference-resolvers/schema/ReferenceTypes';
import { isError, isLoading } from 'reference-resolvers/utils';
import { COMPANIES, CONTACTS, DEALS } from 'reporting-data/constants/dataTypes';
import { RaiseError } from 'reporting-ui-components/trap/ErrorTrap';
import { RaiseLoading } from 'reporting-ui-components/trap/LoadingTrap';
import get from 'transmute/get';
import isEmpty from 'transmute/isEmpty';
import { EVENTS } from '../../lib/events';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getFilterList = reference => {
  const filters = reference.getIn(['referencedObject', 'filters']);
  return filters ? JSON.parse(filters) : [];
};
const RESOLVER_TYPE_FOR_DATA_TYPE = {
  [COMPANIES]: COMPANIES_SAVED_FILTERS,
  [CONTACTS]: CONTACTS_SAVED_FILTERS,
  [DEALS]: DEALS_SAVED_FILTERS
};
function getOptgroup(type, views) {
  const options = views.filter(view => {
    return type.toUpperCase() === view.type.toUpperCase();
  }).map(view => {
    return {
      value: view.id,
      text: view.name
    };
  });
  if (options.length > 0) {
    const labelPath = 'reporting-ui-components.filterEditor.chooseSavedFilters.types';
    return {
      options,
      value: `views_${type}`,
      text: I18n.text(`${labelPath}.${type}`)
    };
  }
  return null;
}
function getOptions(views) {
  return [getOptgroup('standard', views), getOptgroup('my', views), getOptgroup('shared', views)].filter(v => v != null);
}
class SelectedSavedFilters extends PureComponent {
  constructor(props) {
    super(props);
    this.handleSavedFiltersSelected = ({
      target: {
        value
      }
    }) => {
      const {
        onDone,
        savedFilters,
        usageTracker
      } = this.props;
      const savedFilterSet = savedFilters.find(filters => filters.get('id') === String(value));
      usageTracker.track(EVENTS.addSrvSavedFilter, {});
      onDone(getFilterList(savedFilterSet));
    };
    this.handleCancel = () => {
      const {
        onDone
      } = this.props;
      onDone();
    };
    this.renderOptionsSelect = options => {
      const {
        dataType
      } = this.props;
      if (options.every(group => group.length === 0)) {
        return /*#__PURE__*/_jsx(Text, {
          marginInline: "500",
          marginBlock: "200",
          as: "p",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "reporting-ui-components.filterEditor.chooseSavedFilters.noSavedFilters",
            options: {
              dataTypeLabel: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `reporting-ui-components.dataType.${dataType}.plural`
              })
            }
          })
        });
      }
      return /*#__PURE__*/_jsx(UIGroupedTypeahead, {
        onChange: this.handleSavedFiltersSelected,
        options: options,
        minimumSearchCount: 20
      });
    };
    this.renderSavedFilters = () => {
      const {
        savedFilters
      } = this.props;
      const options = getOptions(savedFilters.map(get('referencedObject')).toJS());
      const hasOptions = !isEmpty(options);
      return /*#__PURE__*/_jsxs(Flex, {
        alignItems: "stretch",
        flexDirection: "column",
        hasOptions: hasOptions,
        inlineSize: hasOptions ? '500px' : undefined,
        children: [this.renderOptionsSelect(options), /*#__PURE__*/_jsx(UIButton, {
          className: "m-x-5",
          use: "link",
          onClick: this.handleBack,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-ui-components.filterEditor.chooseSavedFilters.cancel"
          })
        })]
      });
    };
  }
  render() {
    const {
      savedFilters
    } = this.props;
    if (isLoading(savedFilters)) {
      return /*#__PURE__*/_jsx(RaiseLoading, {});
    }
    if (isError(savedFilters)) {
      return /*#__PURE__*/_jsx(RaiseError, {
        error: savedFilters.reason
      });
    }
    return this.renderSavedFilters();
  }
}
const provideSavedFilters = ResolveReferences((resolvers, {
  dataType
}) => {
  const resolver = resolvers[RESOLVER_TYPE_FOR_DATA_TYPE[dataType]];
  return {
    savedFilters: resolver.all()
  };
});
SelectedSavedFilters.propTypes = {
  usageTracker: PropTypes.object,
  onDone: PropTypes.func.isRequired,
  savedFilters: ReferenceTypes.all,
  dataType: PropTypes.string.isRequired
};
export default provideSavedFilters(SelectedSavedFilters);